import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Strikethrough, Italic, List, ListOrdered, Link as LinkIcon, Heading, Maximize2, Minimize2 } from "lucide-react";
import { Toggle } from "@/app/common/common-components/ui/Toggle";
import { Separator } from "@/app/common/common-components/ui/Separator";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/common/common-components/ui/Popover";
import { Input } from "@/app/common/common-components/ui/Input";
import { Button } from "@/app/common/common-components/ui/Button";
import { Markdown } from "tiptap-markdown";
import { Link } from "@tiptap/extension-link";

export interface SimpleTextEditorRef {
  insertTextAtCursor: (text: string) => void;
  focus: () => void;
  getEditor: () => Editor | null;
}

const SimpleTextEditor = forwardRef<
  SimpleTextEditorRef,
  {
    format?: "markdown" | "html";
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
  }
>(({ format = "html", value, onChange, disabled }, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const editor = useEditor({
    editorProps: {
      attributes: {
        class:
          "tw-w-full tw-rounded-md tw-rounded-br-none tw-rounded-bl-none tw-border tw-border-input tw-bg-transparent tw-px-3 tw-py-2 tw-border-b-0 tw-text-sm tw-ring-offset-background tw-placeholder:text-muted-foreground tw-focus-visible:outline-none tw-disabled:cursor-not-allowed tw-disabled:opacity-50 tw-overflow-auto tw-h-full",
      },
    },
    extensions: [
      Markdown,
      Link.configure({
        autolink: false,
        openOnClick: false,
        linkOnPaste: true,
      }),
      StarterKit.configure({
        heading: {
          HTMLAttributes: {
            class: "tw-font-semibold tw-text-3xl",
          },
          levels: [3],
        },
        orderedList: {
          HTMLAttributes: {
            class: "tw-list-decimal tw-pl-4",
          },
        },
        bulletList: {
          HTMLAttributes: {
            class: "tw-list-disc tw-pl-4",
          },
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const editorContent = format === "markdown" ? editor?.storage.markdown.getMarkdown() : editor.getHTML();
      onChange(editorContent);
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      insertTextAtCursor: (text: string) => {
        if (editor) {
          editor.chain().focus().insertContent(text).run();
        }
      },
      focus: () => {
        editor?.chain().focus().run();
      },
      getEditor: () => editor,
    }),
    [editor]
  );

  useEffect(() => {
    const editorContent = format === "markdown" ? editor?.storage.markdown.getMarkdown() : editor?.getHTML();

    if (JSON.stringify(editorContent) === JSON.stringify(value)) return; // Prevent changing the editor content when the value is the same
    editor?.commands.setContent(value);
  }, [value, editor]);

  return (
    <div>
      <div className={`${isExpanded ? "tw-h-[400px]" : "tw-h-[150px]"}`}>
        <EditorContent editor={editor} disabled={disabled} className="tw-h-full" />
      </div>
      {editor ? <RichTextEditorToolbar editor={editor} isExpanded={isExpanded} onToggleExpand={setIsExpanded} /> : null}
    </div>
  );
});

SimpleTextEditor.displayName = "SimpleTextEditor";

const RichTextEditorToolbar = ({
  editor,
  isExpanded,
  onToggleExpand,
}: {
  editor: Editor;
  isExpanded: boolean;
  onToggleExpand: (expanded: boolean) => void;
}) => {
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const handleOpenLinkPopover = () => {
    const currentLink = editor.getAttributes("link").href || "";
    setLinkUrl(currentLink);
    setLinkPopoverOpen(true);
  };

  const handleSetLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setLinkPopoverOpen(false);
  };

  return (
    <div className="tw-flex tw-flex-row tw-items-center tw-gap-1 tw-rounded-bl-md tw-rounded-br-md tw-border tw-border-input tw-bg-transparent tw-p-1">
      <Toggle size="sm" pressed={editor.isActive("bold")} onPressedChange={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="tw-h-4 tw-w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("italic")} onPressedChange={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="tw-h-4 tw-w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("strike")} onPressedChange={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="tw-h-4 tw-w-4" />
      </Toggle>
      <Separator orientation="vertical" className="tw-h-8 tw-w-[1px]" />
      <Toggle size="sm" pressed={editor.isActive("bulletList")} onPressedChange={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="tw-h-4 tw-w-4" />
      </Toggle>
      <Toggle size="sm" pressed={editor.isActive("orderedList")} onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="tw-h-4 tw-w-4" />
      </Toggle>
      <Separator orientation="vertical" className="tw-h-8 tw-w-[1px]" />
      <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen} modal={true}>
        <PopoverTrigger asChild>
          <button className={`tw-p-2 ${editor.isActive("link") ? "tw-text-blue-500" : ""}`} onClick={handleOpenLinkPopover} title="Add or edit link">
            <LinkIcon className="tw-h-4 tw-w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="tw-w-64">
          <div className="tw-flex tw-flex-col tw-gap-2">
            <Input placeholder="Enter URL" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className="tw-w-full" />
            <div className="tw-flex tw-justify-end tw-gap-2">
              <Button variant="outline" onClick={() => setLinkPopoverOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSetLink}>Save</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <Separator orientation="vertical" className="tw-h-8 tw-w-[1px]" />
      <Toggle
        size="sm"
        pressed={editor.isActive("heading", { level: 3 })}
        onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className="tw-px-3 tw-py-2 tw-font-semibold"
      >
        <Heading className="tw-h-4 tw-w-4" />
      </Toggle>
      <Separator orientation="vertical" className="tw-h-8 tw-w-[1px]" />
      <Toggle size="sm" pressed={isExpanded} onPressedChange={onToggleExpand}>
        {isExpanded ? <Minimize2 className="tw-h-4 tw-w-4" /> : <Maximize2 className="tw-h-4 tw-w-4" />}
      </Toggle>
    </div>
  );
};

export default SimpleTextEditor;
